import { Injectable } from '@angular/core';
import * as uuid from 'uuid';

/**
 * AddressCaptureSessionService
 *
 * Mantém o `correlation_id` de uma sessão de captura de endereço (do abrir do
 * modal até o commit do pedido) e o fluxo escolhido, para que o
 * GeocodeTraceService correlacione todos os eventos `ClientGeo*`/`ClientAddress*`
 * de uma mesma tentativa — inclusive os emitidos de dentro dos modais de
 * endereço (singleton compartilhado). Ver
 * exploratorio-taxas-zezinho/wiki/telemetria-zezinho-proposta.md §6.
 */
@Injectable({ providedIn: 'root' })
export class AddressCaptureSessionService {
  private _id = '';
  private _flow = '';
  private _deviceId = '';
  private _coordSource = '';

  /** correlation_id da sessão atual ('' se nenhuma sessão ativa). */
  get id(): string { return this._id; }

  /** Fluxo de endereço da sessão: 'legado' | 'mpboy' | 'highprecision'. */
  get flow(): string { return this._flow; }

  /**
   * Origem FINA da coordenada final da sessão
   * ('autocomplete' | 'heremaps' | 'gps' | 'pin' | 'map-center-default').
   * Propagada para os eventos-âncora (Finalized/Committed) pelo GeocodeTraceService,
   * que só carregavam `coordinate_source_hint = flow` (=mpboy) — insuficiente para
   * separar pin-arrastado (H2) de coord-default ao dismissar (H8).
   */
  get coordSource(): string { return this._coordSource; }
  set coordSource(v: string) { this._coordSource = v || ''; }

  /**
   * Braço do A/B de provedor de geocode ('here' | 'google'), ou null = fora do experimento.
   * Sorteado pela tela multissinal (ver geo-provider-arm.ts) e lido pelo envelope da telemetria.
   * NÃO é limpo em clear(): é por aparelho, não por sessão de captura.
   */
  geoArm: 'here' | 'google' | null = null;

  /**
   * Braço do A/B T20 "confirmar o endereço no início" ('inicio' | 'botao') desta SESSÃO DE
   * CHECKOUT, ou null = fora do experimento. Posto pela delivery.page (ver early-confirm-arm.ts) e
   * lido pelo envelope da telemetria — é o que separa por braço os eventos que já existiam (gate,
   * abandono, abertura da tela). Limpo em clear(): o braço vale para a sessão em que o cliente era
   * elegível, e o pedido seguinte pode nem ser.
   */
  earlyConfirmArm: 'inicio' | 'botao' | null = null;

  /**
   * Braço do A/B A4a "busca do endereço do pino só no confirmar" ('confirmar' | 'arraste'), ou
   * null = fora do experimento. Sorteado pela tela multissinal ao abrir (ver
   * reverse-on-confirm-arm.ts) e lido pelo envelope da telemetria. NÃO é limpo em clear(): é por
   * aparelho, como o `geoArm` — e recalculado a cada abertura, porque o percentual é por loja.
   */
  reverseConfirmArm: 'confirmar' | 'arraste' | null = null;

  /** UUID estável por dispositivo (localStorage), para cohort/dedup. */
  get deviceId(): string {
    if (this._deviceId) return this._deviceId;
    try {
      const KEY = 'geocode_device_id';
      let v = localStorage.getItem(KEY);
      if (!v) { v = uuid.v4(); localStorage.setItem(KEY, v); }
      this._deviceId = v;
    } catch (e) {
      this._deviceId = uuid.v4();
    }
    return this._deviceId;
  }

  /** Inicia uma nova sessão de captura e devolve o novo correlation_id. */
  start(flow: string): string {
    this._id = uuid.v4();
    this._flow = flow || '';
    return this._id;
  }

  /**
   * Garante um correlation_id SEM recriar se já existe (idempotente). Usado nos
   * caminhos que NÃO abrem o modal (endereço pré-preenchido do localStorage):
   * calculateDeliveryFee/goToPaymentPage. Sem isto, os eventos-âncora
   * (ClientFeeCalculated final, ClientAddressFinalized, ClientOrderCommitted)
   * saíam com correlation_id vazio e o pedido ficava órfão da trilha de captura.
   */
  ensureId(flow: string): string {
    if (!this._id) this._id = uuid.v4();
    if (!this._flow && flow) this._flow = flow;
    return this._id;
  }

  clear(): void {
    this._id = '';
    this._flow = '';
    this._coordSource = '';
    this.earlyConfirmArm = null;
  }
}
